/**
 * Bundle 103 - "Sent" tab on the survey campaign page.
 *
 * Data source: GET /api/admin/events/{event_id}/survey/sent
 *
 * Per-recipient dispatched view across ALL send-live invocations
 * for this event. Complements the per-invocation `/survey/send-log`.
 *
 * No em/en dashes, no eslint-disable, no photo/image alt copy.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Eye, EyeOff, Download } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const RESP_BADGE = {
  submitted: 'bg-success-50 text-success-700 border-success-200',
  in_progress: 'bg-blue-50 text-blue-800 border-blue-200',
  not_started: 'bg-neutral-100 text-neutral-600 border-neutral-200',
};

export const SentTabPanel = ({ eventId }) => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unmasked, setUnmasked] = useState(false);
  const [variantFilter, setVariantFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchRows = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const params = {};
      if (variantFilter !== 'all') params.variant = variantFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (opts.unmask) params.unmask = 1;
      const { data } = await api.get(
        `/admin/events/${eventId}/survey/sent`, { params },
      );
      setRows(data?.rows || []);
      setSummary(data?.summary || null);
      if (opts.unmask) setUnmasked(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load Sent view.');
    } finally { setLoading(false); }
  }, [eventId, variantFilter, statusFilter]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const exportCsv = () => {
    const header = ['recipient_name', 'email', 'variant', 'sent_at',
                     'delivered_at', 'opened_at', 'clicked_at',
                     'response_status', 'is_test', 'reissue_status'];
    const lines = [header.join(',')];
    rows.forEach((r) => {
      const cells = [r.recipient_name, r.email, r.variant, r.sent_at || '',
                       r.delivered_at || '', r.opened_at || '',
                       r.clicked_at || '', r.response_status,
                       r.is_test ? '1' : '0', r.reissue_status];
      lines.push(cells.map((c) => `"${(c || '').toString().replace(/"/g, '""')}"`).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `survey-sent-${eventId}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="sent-tab-panel">
      {summary ? (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="sent-summary">
          {[
            ['sent', 'Sent'],
            ['delivered', 'Delivered'],
            ['opened', 'Opened'],
            ['clicked', 'Clicked'],
            ['responded', 'Responded'],
            ['response_rate_pct', 'Rate %'],
          ].map(([k, label]) => (
            <Card key={k}><CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
              <div className="text-lg font-display font-bold text-primary-900 mt-1"
                    data-testid={`sent-summary-${k}`}>
                {summary[k] ?? 0}{k === 'response_rate_pct' ? '%' : ''}
              </div>
            </CardContent></Card>
          ))}
        </div>
      ) : null}

      <div className="flex items-center gap-2 flex-wrap">
        <Select value={variantFilter} onValueChange={(v) => setVariantFilter(v)}>
          <SelectTrigger className="w-40" data-testid="sent-variant-filter"><SelectValue placeholder="Variant" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All variants</SelectItem>
            <SelectItem value="attendee">Attendee</SelectItem>
            <SelectItem value="non_attendee">Non-attendee</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v)}>
          <SelectTrigger className="w-40" data-testid="sent-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All response statuses</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="in_progress">In progress</SelectItem>
            <SelectItem value="not_started">Not started</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline"
                  onClick={() => (unmasked ? setUnmasked(false)
                                           : fetchRows({ unmask: true }))}
                  data-testid="sent-unmask-btn">
          {unmasked ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
          {unmasked ? 'Mask emails' : 'Unmask emails'}
        </Button>
        <Button variant="outline" onClick={exportCsv} data-testid="sent-csv-btn">
          <Download className="h-4 w-4 mr-1" /> CSV
        </Button>
      </div>

      <Card><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm" data-testid="sent-table">
          <thead className="bg-neutral-50 text-left text-xs uppercase tracking-widest text-neutral-500">
            <tr>
              <th className="p-2">Recipient</th>
              <th className="p-2">Email</th>
              <th className="p-2">Variant</th>
              <th className="p-2">Sent</th>
              <th className="p-2">Delivered</th>
              <th className="p-2">Opened</th>
              <th className="p-2">Response</th>
              <th className="p-2">Token</th>
              <th className="p-2">Reissue</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="p-4 text-center text-neutral-500">Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="p-4 text-center text-neutral-500"
                            data-testid="sent-empty">No invitations dispatched yet for this event.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.invitation_id} className="border-t border-neutral-100 hover:bg-neutral-50"
                    data-testid={`sent-row-${r.invitation_id}`}>
                <td className="p-2 text-primary-900">{r.recipient_name}
                  {r.is_test ? <Badge className="ml-1 bg-yellow-50 text-yellow-800 border-yellow-200 border text-[10px]">test</Badge> : null}
                </td>
                <td className="p-2 font-mono text-xs text-neutral-700">{r.email}</td>
                <td className="p-2 text-xs">{r.variant}</td>
                <td className="p-2 text-xs">{r.sent_at ? new Date(r.sent_at).toLocaleString() : '-'}</td>
                <td className="p-2 text-xs">{r.delivered_at ? new Date(r.delivered_at).toLocaleString() : '-'}</td>
                <td className="p-2 text-xs">{r.opened_at ? new Date(r.opened_at).toLocaleString() : '-'}</td>
                <td className="p-2"><Badge className={`${RESP_BADGE[r.response_status] || RESP_BADGE.not_started} border text-[10px]`}>{r.response_status.replace(/_/g, ' ')}</Badge></td>
                <td className="p-2 font-mono text-[10px] text-neutral-500">{r.token_last8}</td>
                <td className="p-2">
                  <Badge variant="outline" className="text-[10px]"
                          data-testid={`sent-row-reissue-${r.invitation_id}`}>
                    {r.reissue_status}
                  </Badge>
                  {r.backfilled_by_bundle_108 ? (
                    <Badge variant="outline"
                            className="ml-1 text-[10px] border-secondary-200 text-secondary-800">
                      b108
                    </Badge>
                  ) : null}
                  {r.replaced_by_invitation_id ? (
                    <div className="text-[10px] text-neutral-500 mt-0.5 font-mono">
                      {'-> '}{r.replaced_by_invitation_id.slice(-8)}
                    </div>
                  ) : null}
                  {r.is_reissue_of ? (
                    <div className="text-[10px] text-neutral-500 mt-0.5 font-mono">
                      from {r.is_reissue_of.slice(-8)}
                    </div>
                  ) : null}
                  {r.invalidated_at ? (
                    <Badge className="ml-1 bg-red-50 text-red-800 border-red-200 border text-[10px]">
                      invalidated
                    </Badge>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
};

export default SentTabPanel;
